import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase-init';

// Shared, single-subscription source for cross-page notification badge counts.
// Before this, Sidebar.jsx (always mounted) AND CommunicationsPage.jsx (its
// own Landing page) each ran their own separate live `conversations` listener
// to compute the same unread-SMS count — doubling reads on that collection
// for the whole time Communications was open. One listener here, shared by
// both via context, instead of N independent ones.
// Exposes both the all-conversations total (what Sidebar's badge always
// showed) and the non-archived total (what Communications' Landing page
// always showed) — same underlying documents, two derived sums, so neither
// consumer's existing behavior changes even though there's now only one
// subscription instead of two.
const NotificationCountsContext = createContext({ unreadSms: 0, unreadSmsActive: 0 });

export function useNotificationCounts() {
  return useContext(NotificationCountsContext);
}

export function NotificationCountsProvider({ children }) {
  const [unreadSms, setUnreadSms] = useState(0);
  const [unreadSmsActive, setUnreadSmsActive] = useState(0);

  useEffect(() => {
    const q = query(collection(db, 'conversations'), where('unreadCount', '>', 0));
    const unsub = onSnapshot(q, snap => {
      let all = 0, active = 0;
      snap.docs.forEach(d => {
        const data = d.data();
        const count = data.unreadCount || 0;
        all += count;
        if (!data.archived) active += count;
      });
      setUnreadSms(all);
      setUnreadSmsActive(active);
    });
    return () => unsub();
  }, []);

  return (
    <NotificationCountsContext.Provider value={{ unreadSms, unreadSmsActive }}>
      {children}
    </NotificationCountsContext.Provider>
  );
}
